import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  SingleSelectOption,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { FormsModule } from '@angular/forms';
import {
  AuthenticationConfiguration,
  CreateSecretRequest,
  Integration,
} from '../../../../../api/models';
import { CommonModule } from '@angular/common';
import {
  AuthenticationVersion,
  AuthInputValues,
  AuthTypeValues,
  DEFAULT_AUTH_VALUES,
  DialogKey,
  UpdateOrganizationParams,
} from '../../../../../types/types';
import { AwAuthFormComponent } from '../../../components/aw-auth-form/aw-auth-form.component';
import { AuthenticationService } from '../../../../../core/services/authentication.service';
import { catchError, finalize, forkJoin, Observable, of, switchMap } from 'rxjs';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { buildAuthSecretName } from '../../../../../shared/utils/auth.utils';
import { SecretService } from '../../../../../core/services/secret.service';
import {
  buildClientSecretName,
  computeDaysToExpire,
  getSecretExpiryStatus,
} from '../../../../../shared/utils/secrets.utils';
import { CreateClientSecret$Params } from '../../../../../api/fn/client-secret-management/create-client-secret';
import { ClientSecretService } from '../../../../../core/services/client-secret.service';
import { DeleteClientSecret$Params } from '../../../../../api/fn/client-secret-management/delete-client-secret';
import { ThemeService } from '../../../../../core/services/theme.service';
import { EmployeeAuthenticationService } from '../../../../../core/services/employee-authentication.service';
import {
  Organization,
  UserService,
} from '../../../../../core/services/user.service';

import { UnsavedChangesForm } from '../../../../../shared/unsaved-changes/unsaved-changes-form';
import { UnsavedChangesDialogComponent } from '../../../../../shared/unsaved-changes/unsaved-changes-dialog.component';
import { isValidOrganizationName, ORGANIZATION_NAME_MAX_LENGTH, ORGANIZATION_NAME_PATTERN, ORGANIZATION_NAME_PATTERN_MESSAGE } from '../../../../../shared/utils/organization-name';
@Component({
    selector: 'app-edit-integration-details',
    imports: [
        UnsavedChangesDialogComponent,
        AwDividerComponent,
        AwTextFormComponent,
        AwButtonDirective,
        AwTableComponent,
        AwDialogComponent,
        AwDropdownFormComponent,
        AwBreadCrumbComponent,
        AwActionBarComponent,
        AwSpinnerComponent,
        AwAuthFormComponent,
        AwToastComponent,
        FormsModule,
        CommonModule,
    ],
    templateUrl: './edit-integration-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './edit-integration-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class EditIntegrationDetailsComponent extends UnsavedChangesForm implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly integrationService = inject(IntegrationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly employeeAuthenticationService = inject(EmployeeAuthenticationService);
  private readonly secretService = inject(SecretService);
  private readonly clientSecretService = inject(ClientSecretService);
  private readonly themeService = inject(ThemeService);
  private readonly userService = inject(UserService);

  readonly nameMaxLength = ORGANIZATION_NAME_MAX_LENGTH;
  readonly namePattern = ORGANIZATION_NAME_PATTERN;
  readonly namePatternMessage = ORGANIZATION_NAME_PATTERN_MESSAGE;

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  container_theme = computed(() => this.themeService.getTheme());

  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  authTypeOptions = signal<SingleSelectOption[]>([
    {
      label: 'OAuth2',
    },
    {
      label: 'EntraID',
    },
    {
      label: 'OpenID Connect',
    },
  ]);

  integrationInfo!: Integration | undefined;

  organization: Organization | null = null;

  selectedDate: Date | null = null;
  integrationNameInput: string = '';
  integrationIdInput: string = '';
  integrationStatusInput: string = '';
  integrationStatusValue: boolean | null = null;
  apiAuthTypeInput: string = '';
  apiAuthTypeValue: AuthTypeValues | undefined;

  employeeAuthTypeInput: string = '';
  employeeAuthTypeValue: AuthTypeValues | undefined;

  apiAuthFieldsFilled: boolean = false;
  apiAuthAttempted: boolean = false;

  employeeAuthFieldsFilled: boolean = false;
  employeeAuthAttempted: boolean = false;

  hasEmployeeAuthConfig: boolean = false;

  openDeleteIntegrationSecretDialog: boolean = false;
  openAddIntegrationSecretDialog: boolean = false;
  openClientSecretDialog: boolean = false;
  secretToDelete: TableRow | null = null;
  generatedClientSecret: string = '';

  userPermissions: Record<string, boolean> = {};

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () =>
          this.router.navigate([
            `/integration-management/integration-details/${this.integrationIdInput}`,
          ]),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => {
          this.onEditIntegration();
        },
      },
    },
  ]);

  breadcrumbs = signal<BreadCrumb[]>([]);

  oauthApiAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  entraApiAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  openIdApiAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  oAuthApiSecretName: string = '';
  entraApiSecretName: string = '';
  openIdApiSecretName: string = '';

  oauthEmployeeAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  entraEmployeeAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  openIdEmployeeAuthValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  oAuthEmployeeSecretName: string = '';
  entraEmployeeSecretName: string = '';
  openIdEmployeeSecretName: string = '';

  get selectedApiAuthValues(): AuthInputValues {
    switch (this.apiAuthTypeValue) {
      case AuthTypeValues.OAUTH2:
        return this.oauthApiAuthValues;
      case AuthTypeValues.ENTRAID:
        return this.entraApiAuthValues;
      case AuthTypeValues.OPENIDCONNECT:
        return this.openIdApiAuthValues;
      default:
        return DEFAULT_AUTH_VALUES;
    }
  }

  get selectedEmployeeAuthValues(): AuthInputValues {
    switch (this.employeeAuthTypeValue) {
      case AuthTypeValues.OAUTH2:
        return this.oauthEmployeeAuthValues;
      case AuthTypeValues.ENTRAID:
        return this.entraEmployeeAuthValues;
      case AuthTypeValues.OPENIDCONNECT:
        return this.openIdEmployeeAuthValues;
      default:
        return DEFAULT_AUTH_VALUES;
    }
  }

  secretsColumnsDefinition: TableCellInput[] = [
    {
      type: TableCellTypes.Title,
      key: 'client_id',
      label: 'Client ID',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'client_secret_name',
      label: 'Expiration Date',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'days_remaining',
      label: 'Days until Expiration',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.IconButton,
      key: 'random',
      label: 'Remove',
      buttonType: 'primary',
      isExpanded: false,
      icon: 'delete',
      action: (data: TableRow) => {
        this.openDeleteIntegrationSecretDialog = true;
        this.secretToDelete = data;
      },
    },
  ];

  secretsTableData = signal<any[]>([]);
  secretsTableDataCopy: any[] = [];

  formFieldsData = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'client_secret_name',
        label: 'Expiration Date',
        value: buildClientSecretName(),
        type: 'text',
        readOnly: true,
      },
    ],
  });

  deleteSecretDialogOptions = input<DialogOptions>({
    variant: 'alert',
    title: 'Delete Third-Party Integration Secret',
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  });

  addSecretDialogOptions = input<DialogOptions>({
    variant: 'standard',
    title: 'Add Third-Party Integration Secret',
    description: '',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Add',
  });

  clientSecretDialogOptions = input<DialogOptions>({
    variant: 'alert',
    title: 'Client Secret Created',
    description: 'Keep track of this information. It will not be provided again after this.',
    primaryButtonLabel: 'Close',
  });

  clientSecretFormFields: DialogFormControls = {
    controls: [],
  };

  dialogFormCloseHandler(key: DialogKey) {
    if (key === 'add') {
      this.openAddIntegrationSecretDialog = false;
    } else if (key === 'delete') {
      this.openDeleteIntegrationSecretDialog = false;
    } else if (key === 'client-secret') {
      this.openClientSecretDialog = false;
      this.generatedClientSecret = '';
    }
  }

  fillAuthValues(
    authData: AuthenticationConfiguration,
    authenticationVersion: AuthenticationVersion
  ) {
    if (authData.entraIdConfiguration) {
      const entraConfig = authData.entraIdConfiguration;
      const entraAuthValues: AuthInputValues = {
        auth_endpoint: entraConfig.authorizationEndpoint,
        token_endpoint: entraConfig.tokenEndpoint,
        logout_endpoint: entraConfig.logoutEndpoint ?? '',
        clientId: entraConfig.clientId,
        grant_type: entraConfig?.grantType ?? '',
        scope: entraConfig?.scope ?? '',
        client_secret: '',
        ext_userId_claim: entraConfig.externalUserIdClaimName,
        self_discovery_endpoint: entraConfig.selfDiscoveryEndpoint,
        tenantId: entraConfig.tenantId,
      };
      if (authenticationVersion === 'api') {
        this.entraApiAuthValues = entraAuthValues;
        this.entraApiSecretName = entraConfig.clientSecretReference;
      } else if (authenticationVersion === 'employee') {
        this.entraEmployeeAuthValues = entraAuthValues;
        this.entraEmployeeSecretName = entraConfig.clientSecretReference;
        this.employeeAuthTypeValue = AuthTypeValues.ENTRAID;
        this.employeeAuthTypeInput = 'EntraID';
      }
    }
    if (authData.oAuth2Configuration) {
      const oAuthConfig = authData.oAuth2Configuration;
      const oauthAuthValues: AuthInputValues = {
        auth_endpoint: oAuthConfig.authorizationEndpoint ?? '',
        token_endpoint: oAuthConfig.tokenEndpoint,
        clientId: oAuthConfig.clientId,
        grant_type: oAuthConfig?.grantType ?? 'client_credentials',
        scope: oAuthConfig?.scope ?? '',
        client_secret: '',
      };
      if (authenticationVersion === 'api') {
        this.oauthApiAuthValues = oauthAuthValues;
        this.oAuthApiSecretName = oAuthConfig.clientSecretReference;
      } else if (authenticationVersion === 'employee') {
        this.oauthEmployeeAuthValues = oauthAuthValues;
        this.oAuthEmployeeSecretName = oAuthConfig.clientSecretReference;
        this.employeeAuthTypeValue = AuthTypeValues.OAUTH2;
        this.employeeAuthTypeInput = 'OAuth2';
      }
    }
    if (authData.openIdConnectConfiguration) {
      const openIdConfig = authData.openIdConnectConfiguration;
      const openIdAuthValues: AuthInputValues = {
        auth_endpoint: openIdConfig.authorizationEndpoint,
        token_endpoint: openIdConfig.tokenEndpoint,
        logout_endpoint: openIdConfig.logoutEndpoint ?? '',
        clientId: openIdConfig.clientId,
        grant_type: openIdConfig?.grantType ?? '',
        scope: openIdConfig?.scope ?? '',
        client_secret: '',
        ext_userId_claim: openIdConfig.externalUserIdClaimName,
        self_discovery_endpoint: openIdConfig.selfDiscoveryEndpoint,
      };
      if (authenticationVersion === 'api') {
        this.openIdApiAuthValues = openIdAuthValues;
        this.openIdApiSecretName = openIdConfig.clientSecretReference;
      } else if (authenticationVersion === 'employee') {
        this.openIdEmployeeAuthValues = openIdAuthValues;
        this.openIdEmployeeSecretName = openIdConfig.clientSecretReference;
        this.employeeAuthTypeValue = AuthTypeValues.OPENIDCONNECT;
        this.employeeAuthTypeInput = 'OpenID Connect';
      }
    }
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.integrationIdInput =
      this.route.snapshot.paramMap.get('integrationId')!;

    this.organization = this.userService.getOrganization();

    this.userPermissions = this.userService.getUserPermissions();

    const integrationSecrets = this.userPermissions['isAdministrator']
      ? this.clientSecretService
          .getClientSecrets({
            organizationId: this.integrationIdInput,
          })
          .pipe(
            catchError((error) => {
              return of(null);
            })
          )
      : of(null);

    forkJoin({
      integration: this.integrationService
        .getIntegrationById({ organizationId: this.integrationIdInput })
        .pipe(
          catchError((error) => {
            return of(null);
          })
        ),
      apiAuthData: this.authenticationService
        .getAuthenticationConfig({ organizationId: this.integrationIdInput })
        .pipe(
          catchError((error) => {
            return of(null);
          })
        ),
      employeeAuthData: this.employeeAuthenticationService
        .getAuthenticationConfig({ organizationId: this.integrationIdInput })
        .pipe(
          catchError((error) => {
            return of(null);
          })
        ),
      integrationSecrets: integrationSecrets,
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        ({
          integration,
          apiAuthData,
          employeeAuthData,
          integrationSecrets,
        }) => {
          if (!integration) {
            this.buildToastMessage('Third-party integration not found', 'alert');
            return;
          }

          this.integrationInfo = integration;

          this.apiAuthTypeValue = integration.authenticationType;

          if (this.apiAuthTypeValue === AuthTypeValues.OAUTH2) {
            this.apiAuthTypeInput = 'OAuth2';
          } else if (this.apiAuthTypeValue === AuthTypeValues.ENTRAID) {
            this.apiAuthTypeInput = 'EntraID';
          } else if (this.apiAuthTypeValue === AuthTypeValues.OPENIDCONNECT) {
            this.apiAuthTypeInput = 'OpenID Connect';
          }

          this.integrationNameInput = integration.organizationName || '';
          this.integrationStatusValue = integration.enabled || null;
          this.integrationStatusInput = integration.enabled
            ? 'Active'
            : 'Disabled';

          if (apiAuthData) {
            this.fillAuthValues(apiAuthData, 'api');
          } else {
            this.buildToastMessage(
              'No API authentication configuration found for third-party integration',
              'alert'
            );
          }

          if (employeeAuthData) {
            this.hasEmployeeAuthConfig = true;
            this.fillAuthValues(employeeAuthData, 'employee');
          } else {
            this.hasEmployeeAuthConfig = false;
            this.buildToastMessage(
              'No third-party employee authentication configuration found for third-party integration',
              'alert'
            );
          }

          if (integrationSecrets) {
            const integrationSecretsData: any[] = Object.entries(
              integrationSecrets
            ).map(([key, value]) => ({
              client_id: this.integrationIdInput,
              client_secret_name: key,
              days_remaining: `${computeDaysToExpire(value)} days`,
              badgeNested: getSecretExpiryStatus(value),
            }));
            this.secretsTableData.set(integrationSecretsData);
            this.secretsTableDataCopy = integrationSecretsData;
          } else if (this.userPermissions['isAdministrator']) {
            this.buildToastMessage(
              'No client secrets found for third-party integration',
              'alert'
            );
          }
          this.markCleanAfterRender();
        }
      );

    const baseBreadcrumbs: BreadCrumb[] = [];
    
    if (this.organization === 'assetworks-employee') {
      baseBreadcrumbs.push({
        label: 'Third-Party Integration Management',
        route: '/integration-management/overview',
        newTab: false,
      });
    }

    this.breadcrumbs.set([
      ...baseBreadcrumbs,
      {
        label: 'Third-Party Integration Details',
        route: `/integration-management/integration-details/${this.integrationIdInput}`,
        newTab: false,
      },
      {
        label: 'Edit Third-Party Integration Details',
        route: `/integration-management/integration-details/${this.integrationIdInput}/edit`,
        newTab: false,
      },
    ]);
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  buildAuthConfig(
    authTypeValue: AuthTypeValues | undefined,
    authenticationVersion: AuthenticationVersion
  ): AuthInputValues {
    let authConfig: AuthInputValues = DEFAULT_AUTH_VALUES;
    if (authenticationVersion === 'api') {
      if (authTypeValue === AuthTypeValues.OAUTH2) {
        authConfig = this.oauthApiAuthValues;
      } else if (authTypeValue === AuthTypeValues.ENTRAID) {
        authConfig = this.entraApiAuthValues;
      } else if (authTypeValue === AuthTypeValues.OPENIDCONNECT) {
        authConfig = this.openIdApiAuthValues;
      }
    } else if (authenticationVersion === 'employee') {
      if (authTypeValue === AuthTypeValues.OAUTH2) {
        authConfig = this.oauthEmployeeAuthValues;
      } else if (authTypeValue === AuthTypeValues.ENTRAID) {
        authConfig = this.entraEmployeeAuthValues;
      } else if (authTypeValue === AuthTypeValues.OPENIDCONNECT) {
        authConfig = this.openIdEmployeeAuthValues;
      }
    }

    return authConfig;
  }

  getAuthSecretName(
    authType: AuthTypeValues | undefined,
    authenticationVersion: AuthenticationVersion
  ): string {
    return buildAuthSecretName(authType, authenticationVersion);
  }

  validateIntegrationFields(): boolean {
    return (
      [
        AuthTypeValues.OAUTH2,
        AuthTypeValues.ENTRAID,
        AuthTypeValues.OPENIDCONNECT,
      ].includes(this.apiAuthTypeValue as number) &&
      this.apiAuthTypeInput !== '' &&
      isValidOrganizationName(this.integrationNameInput) &&
      this.integrationStatusValue !== null &&
      this.integrationStatusInput !== '' &&
      this.integrationIdInput !== ''
    );
  }

  onEditIntegration(): void {
    // Special handling for integration-employees
    if (this.organization === 'integration-employee') {
      const hasApiSecret = !!this.selectedApiAuthValues.client_secret;
      const hasEmployeeSecret = !!this.selectedEmployeeAuthValues.client_secret;
      
      if (!hasApiSecret && !hasEmployeeSecret) {
        this.buildToastMessage('No changes to save', 'alert');
        return;
      }
      
      this.updateSecretsOnly();
      return;
    }

    if (!this.validateIntegrationFields()) {
      this.buildToastMessage(
        'Fill in all required third-party integration fields',
        'error'
      );
      return;
    }

    if (this.apiAuthAttempted && !this.apiAuthFieldsFilled) {
      this.buildToastMessage(
        'Fill in all required API authentication fields',
        'error'
      );
      return;
    }

    if (this.employeeAuthAttempted && !this.employeeAuthFieldsFilled) {
      this.buildToastMessage(
        'Fill in all required third-party employee authentication fields',
        'error'
      );
      return;
    }

    const integrationParams: UpdateOrganizationParams = {
      organizationAuthType: this.apiAuthTypeValue,
      organizationName: this.integrationNameInput,
      organizationStatus: this.integrationStatusValue ?? false,
      organizationPartnerIds: this.integrationInfo?.customerIds,
      organizationId: this.integrationIdInput,
    };

    const apiAuthConfig: AuthInputValues = this.buildAuthConfig(
      this.apiAuthTypeValue,
      'api'
    );
    const employeeAuthConfig: AuthInputValues = this.buildAuthConfig(
      this.employeeAuthTypeValue,
      'employee'
    );

    const apiAuthSecretName: string = this.getAuthSecretName(
      this.apiAuthTypeValue,
      'api'
    );
    const employeeAuthSecretName: string = this.getAuthSecretName(
      this.employeeAuthTypeValue,
      'employee'
    );

    const apiAuthSecretInfo: CreateSecretRequest = {
      secretName: apiAuthSecretName,
      secretValue: apiAuthConfig.client_secret ?? `${apiAuthSecretName}Value`,
    };
    const employeeAuthSecretInfo: CreateSecretRequest = {
      secretName: employeeAuthSecretName,
      secretValue:
        employeeAuthConfig.client_secret ?? `${employeeAuthSecretName}Value`,
    };

    console.log(employeeAuthSecretInfo);
    const requests: { [key: string]: Observable<any> } = {};

    if (this.userPermissions['canEdit']) {
      requests['integrationDetailsUpdated'] =
        this.integrationService.updateIntegration(integrationParams);
    }

    if (this.apiAuthFieldsFilled) {
      if (this.userPermissions['canEdit']) {
        requests['apiAuthConfigUpdated'] =
          this.authenticationService.updateAuthenticationConfig({
            organizationId: this.integrationIdInput,
            authType: this.apiAuthTypeValue,
            authConfigValues: apiAuthConfig,
            secretName: apiAuthSecretName,
          });
      }
      if (apiAuthConfig.client_secret) {
        requests['apiSecretNameUpdated'] = this.secretService.createSecret({
          organizationId: this.integrationIdInput,
          body: apiAuthSecretInfo,
        });
      }
    }

    if (this.employeeAuthFieldsFilled) {
      if (this.userPermissions['canEdit']) {
        if (this.hasEmployeeAuthConfig) {
          const hasSecretChanged = employeeAuthConfig.client_secret && employeeAuthConfig.client_secret.trim() !== '';

          if (hasSecretChanged) {
            requests['employeeAuthConfigUpdated'] = this.secretService.createSecret({
              organizationId: this.integrationIdInput,
              body: employeeAuthSecretInfo,
            }).pipe(
              switchMap(() => this.employeeAuthenticationService.updateAuthenticationConfig({
                organizationId: this.integrationIdInput,
                authType: this.employeeAuthTypeValue,
                authConfigValues: employeeAuthConfig,
                secretName: employeeAuthSecretName,
              }))
            );
          } else {
            requests['employeeAuthConfigUpdated'] = this.employeeAuthenticationService.updateAuthenticationConfig({
              organizationId: this.integrationIdInput,
              authType: this.employeeAuthTypeValue,
              authConfigValues: employeeAuthConfig,
              secretName: employeeAuthSecretName,
            });
          }
        } else {
          requests['employeeAuthConfigUpdated'] = this.secretService.createSecret({
            organizationId: this.integrationIdInput,
            body: employeeAuthSecretInfo,
          }).pipe(
            switchMap(() => this.employeeAuthenticationService.createAuthenticationConfig({
              organizationId: this.integrationIdInput,
              authType: this.employeeAuthTypeValue,
              authConfigValues: employeeAuthConfig,
              secretName: employeeAuthSecretName,
            }))
          );
        }
      }
    }

    this.isLoading = true;
    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((results) => {
        // Everything sent saved: the form's current values become the clean state.
        if (Object.values(results).every(Boolean)) {
          this.markClean();
        }
        const apiAuthConfigUpdated = results['apiAuthConfigUpdated'];
        const apiSecretNameUpdated = results['apiSecretNameUpdated'];

        const employeeAuthConfigUpdated = results['employeeAuthConfigUpdated'];
        const employeeSecretNameUpdated = results['employeeSecretNameUpdated'];

        const integrationDetailsUpdated = results['integrationDetailsUpdated'];
        if (this.organization === 'assetworks-employee') {
          if (this.apiAuthFieldsFilled) {
            if (apiAuthConfigUpdated && integrationDetailsUpdated) {
              this.buildToastMessage(
                'Successfully updated third-party integration and authentication',
                'success'
              );
            } else if (apiAuthConfigUpdated && !integrationDetailsUpdated) {
              this.buildToastMessage(
                'Successfully updated authentication but failed to update third-party integration',
                'alert'
              );
            } else if (!apiAuthConfigUpdated && integrationDetailsUpdated) {
              this.buildToastMessage(
                'Successfully updated third-party integration but failed to update authentication',
                'alert'
              );
            } else {
              this.buildToastMessage(
                'Failed to update third-party integration and authentication',
                'error'
              );
            }
          } else if (this.employeeAuthFieldsFilled) {
            if (employeeAuthConfigUpdated && integrationDetailsUpdated) {
              this.buildToastMessage(
                'Successfully updated third-party integration and authentication',
                'success'
              );
            } else if (
              employeeAuthConfigUpdated &&
              !integrationDetailsUpdated
            ) {
              this.buildToastMessage(
                'Successfully updated authentication but failed to update third-party integration',
                'alert'
              );
            } else if (
              !employeeAuthConfigUpdated &&
              integrationDetailsUpdated
            ) {
              this.buildToastMessage(
                'Successfully updated third-party integration but failed to update authentication',
                'alert'
              );
            } else {
              this.buildToastMessage(
                'Failed to update third-party integration and authentication',
                'error'
              );
            }
          } else {
            if (integrationDetailsUpdated) {
              this.buildToastMessage(
                'Successfully updated third-party integration',
                'success'
              );
            } else {
              this.buildToastMessage('Failed to update third-party integration', 'error');
            }
          }
        } else if (this.organization === 'integration-employee') {
          if (employeeSecretNameUpdated && apiSecretNameUpdated) {
            this.buildToastMessage(
              'Successfully updated third-party employee and API secrets',
              'success'
            );
          } else if (employeeSecretNameUpdated) {
            this.buildToastMessage(
              'Successfully updated third-party employee secret',
              'success'
            );
          } else if (apiSecretNameUpdated) {
            this.buildToastMessage(
              'Successfully updated API secret',
              'success'
            );
          } else {
            this.buildToastMessage('Failed to update secret', 'error');
          }
        }
      });
  }

  addSecretFormSubmit(formValues: any) {
    if (!formValues.valid) {
      this.buildToastMessage('Please fill in all required fields', 'error');
      return;
    }

    this.isLoading = true;
    const newClientSecretName = formValues.formData.client_secret_name;
    const params: CreateClientSecret$Params = {
      organizationId: this.integrationIdInput,
      body: {
        clientSecretName: newClientSecretName,
      },
    };

    this.clientSecretService
      .createClientSecret(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((clientSecretCreated) => {
        if (!clientSecretCreated || !clientSecretCreated.clientSecret) {
          this.buildToastMessage('Failed to create client secret', 'error');
          return;
        }

        this.generatedClientSecret = clientSecretCreated?.clientSecret ?? '';
        this.clientSecretFormFields = {
          controls: [
            {
              formControlName: 'client_secret',
              label: 'Client Secret',
              value: this.generatedClientSecret,
              type: 'text',
              controlRequired: false,
              readOnly: true,
              fieldActions: [
                {
                  iconButtonCallback: {
                    action: () => this.copyToClipboard(this.generatedClientSecret),
                    icon: "copy",
                    label: "Click to copy the client secret to the clipboard",
                  }
                }
              ],
            },
          ],
        };
        this.openClientSecretDialog = true;
        this.openAddIntegrationSecretDialog = false;

        this.secretsTableData.update((secrets) => [
          ...secrets,
          {
            client_id: this.integrationIdInput,
            client_secret_name: newClientSecretName,
            days_remaining: `${computeDaysToExpire(newClientSecretName)} days`,
            badgeNested: {
              color: 'success-green-700',
              label: 'Active',
            },
          },
        ]);
      });
  }

  deleteSecretFormSubmit(formValues: any) {
    this.isLoading = true;
    const params: DeleteClientSecret$Params = {
      organizationId: this.integrationIdInput,
      clientSecretName: this.secretToDelete?.['client_secret_name'] || '',
    };
    this.clientSecretService
      .deleteClientSecret(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((clientSecretDeleted) => {
        if (!clientSecretDeleted) {
          this.buildToastMessage('Failed to delete client secret', 'error');
          return;
        }
        this.buildToastMessage('Successfully deleted client secret', 'success');
        this.openDeleteIntegrationSecretDialog = false;

        const filtered = this.secretsTableDataCopy.filter(
          (secret) =>
            secret.client_secret_name !==
            this.secretToDelete?.['client_secret_name']
        );
        this.secretsTableData.set(filtered);
        this.secretsTableDataCopy = [...filtered];
      });
  }

  onApiAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    if (this.apiAuthTypeValue === AuthTypeValues.OAUTH2) {
      this.oauthApiAuthValues = event.values;
    } else if (this.apiAuthTypeValue === AuthTypeValues.ENTRAID) {
      this.entraApiAuthValues = event.values;
    } else if (this.apiAuthTypeValue === AuthTypeValues.OPENIDCONNECT) {
      this.openIdApiAuthValues = event.values;
    }

    this.apiAuthFieldsFilled = event.fieldsFilled;
    this.apiAuthAttempted = event.someFieldsFilled;
  }

  onEmployeeAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    if (this.employeeAuthTypeValue === AuthTypeValues.OAUTH2) {
      this.oauthEmployeeAuthValues = event.values;
    } else if (this.employeeAuthTypeValue === AuthTypeValues.ENTRAID) {
      this.entraEmployeeAuthValues = event.values;
    } else if (this.employeeAuthTypeValue === AuthTypeValues.OPENIDCONNECT) {
      this.openIdEmployeeAuthValues = event.values;
    }

    this.employeeAuthFieldsFilled = event.fieldsFilled;
    this.employeeAuthAttempted = event.someFieldsFilled;
  }

  onDetailsTextEntrySelected(text: string | null, key: string) {
    if (key === 'integration-name') {
      this.integrationNameInput = text || '';
    }
  }

  onIntegrationStatusSelected(status: SingleSelectOption | null) {
    this.integrationStatusInput = status?.label || '';
    if (status?.label === 'Active') {
      this.integrationStatusValue = true;
    } else if (status?.label === 'Disabled') {
      this.integrationStatusValue = false;
    }
  }

  onIntegrationAuthTypeSelected(authType: SingleSelectOption | null) {
    this.apiAuthTypeInput = authType?.label || '';
    if (authType?.label === 'OAuth2') {
      this.apiAuthTypeValue = AuthTypeValues.OAUTH2;
    } else if (authType?.label === 'EntraID') {
      this.apiAuthTypeValue = AuthTypeValues.ENTRAID;
    } else if (authType?.label === 'OpenID Connect') {
      this.apiAuthTypeValue = AuthTypeValues.OPENIDCONNECT;
    }
  }

  onEmployeeAuthTypeSelected(authType: SingleSelectOption | null) {
    this.employeeAuthTypeInput = authType?.label || '';
    if (authType?.label === 'OAuth2') {
      this.employeeAuthTypeValue = AuthTypeValues.OAUTH2;
    } else if (authType?.label === 'EntraID') {
      this.employeeAuthTypeValue = AuthTypeValues.ENTRAID;
    } else if (authType?.label === 'OpenID Connect') {
      this.employeeAuthTypeValue = AuthTypeValues.OPENIDCONNECT;
    }
  }

  integrationTextForms = computed(() => [
    {
      title: 'Third-Party Integration Name',
      isName: true,
      required: true,
      readOnly: this.organization === 'integration-employee',
      value: this.integrationInfo?.organizationName ?? '',
      textEntryActionHandler: (text: string | null) =>
        this.onDetailsTextEntrySelected(text, 'integration-name'),
    },
    {
      title: 'Integration ID',
      readOnly: true,
      required: true,
      value: this.integrationInfo?.organizationId ?? '',
      textEntryActionHandler: (text: string | null) => {},
    },
  ]);

  updateSecretsOnly(): void {
    this.isLoading = true;
    
    const requests: Observable<any>[] = [];
    
    if (this.selectedApiAuthValues.client_secret) {
      const apiSecretName = buildAuthSecretName(this.apiAuthTypeValue, 'api');
      requests.push(this.secretService.createSecret({
        organizationId: this.integrationIdInput,
        body: { secretName: apiSecretName, secretValue: this.selectedApiAuthValues.client_secret }
      }));
    }
    
    if (this.selectedEmployeeAuthValues.client_secret) {
      const employeeSecretName = buildAuthSecretName(this.employeeAuthTypeValue, 'employee');
      requests.push(this.secretService.createSecret({
        organizationId: this.integrationIdInput,
        body: { secretName: employeeSecretName, secretValue: this.selectedEmployeeAuthValues.client_secret }
      }));
    }
    
    if (requests.length === 0) {
      this.isLoading = false;
      this.buildToastMessage('No changes to save', 'alert');
      return;
    }
    
    forkJoin(requests).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.buildToastMessage('Successfully updated secrets', 'success');
          this.router.navigate([`/integration-management/integration-details/${this.integrationIdInput}`]);
        },
        error: () => {
          this.buildToastMessage('Failed to update secrets', 'error');
        }
      });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text)
      .then(() => {
        this.buildToastMessage('Copied to clipboard', 'success');
      })
      .catch(() => {
        this.buildToastMessage('Failed to copy to clipboard', 'error');
      });
  }

  protected formState(): unknown {
    return {
      name: this.integrationNameInput,
      status: this.integrationStatusInput,
      apiAuthType: this.apiAuthTypeInput,
      employeeAuthType: this.employeeAuthTypeInput,
      apiAuth: [this.oauthApiAuthValues, this.entraApiAuthValues, this.openIdApiAuthValues],
      employeeAuth: [this.oauthEmployeeAuthValues, this.entraEmployeeAuthValues, this.openIdEmployeeAuthValues],
    };
  }
}
